import { useState, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    UserPlus, Search, Edit, Trash2, User,
    Users, Briefcase, Link, Upload, X, Image as ImageIcon, Loader2
} from 'lucide-react';
import Card from '../../components/Card';
import Table from '../../components/Table';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Select from '../../components/Select';
import Badge from '../../components/Badge';
import Modal from '../../components/Modal';
import {
    getCustomPeople,
    createCustomPerson,
    updateCustomPerson,
    deleteCustomPerson,
    searchGeniusPlayers,
    searchGeniusStaff,
    uploadPersonImage
} from '../../utils/api';
import toast from 'react-hot-toast';

// Person form component
function PersonForm({ person, onSubmit, onCancel, isLoading }) {
    const [formData, setFormData] = useState({
        geniusPersonId: person?.geniusPersonId?.toString() || '',
        personType: person?.personType || 'player',
        firstName: person?.firstName || '',
        lastName: person?.lastName || '',
        position: person?.position || '',
        shirtNumber: person?.shirtNumber || '',
        height: person?.height || '',
        nationality: person?.nationality || '',
        nationalityCode: person?.nationalityCode || '',
        staffRole: person?.staffRole || '',
        imageUrl: person?.imageUrl || '',
        imageUrlLow: person?.imageUrlLow || ''
    });

    const [geniusSearch, setGeniusSearch] = useState('');
    const [showGeniusSearch, setShowGeniusSearch] = useState(!person);
    const [imagePreview, setImagePreview] = useState(person?.imageUrl || null);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef(null);

    // Search genius people
    const { data: geniusResults, isLoading: geniusLoading } = useQuery({
        queryKey: ['genius-search', geniusSearch, formData.personType],
        queryFn: () => formData.personType === 'player'
            ? searchGeniusPlayers(geniusSearch)
            : searchGeniusStaff(geniusSearch),
        enabled: geniusSearch.length >= 2
    });

    const handleGeniusSelect = (geniusPerson) => {
        setFormData({
            ...formData,
            geniusPersonId: geniusPerson.geniusPersonId.toString(),
            firstName: geniusPerson.firstName,
            lastName: geniusPerson.lastName,
            position: geniusPerson.position || '',
            shirtNumber: geniusPerson.shirtNumber || '',
            nationality: geniusPerson.nationality || '',
            imageUrl: geniusPerson.imageUrl || ''
        });
        if (geniusPerson.imageUrl) {
            setImagePreview(geniusPerson.imageUrl);
        }
        setShowGeniusSearch(false);
        setGeniusSearch('');
    };

    const handleImageSelect = useCallback(async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Invalid file type. Only JPEG, PNG, GIF, and WebP are allowed.');
            return;
        }

        // Validate file size (10MB)
        if (file.size > 10 * 1024 * 1024) {
            toast.error('File size exceeds 10MB limit');
            return;
        }

        // Show preview immediately
        const previewUrl = URL.createObjectURL(file);
        setImagePreview(previewUrl);
        setIsUploading(true);

        try {
            const result = await uploadPersonImage(file);

            if (result.success) {
                setFormData(prev => ({
                    ...prev,
                    imageUrl: result.data.imageUrl,
                    imageUrlLow: result.data.imageUrlLow
                }));
                // Update preview to actual uploaded URL
                setImagePreview(result.data.imageUrl);
                toast.success('Image uploaded successfully');
            } else {
                throw new Error('Upload failed');
            }
        } catch (error) {
            toast.error(error.message || 'Failed to upload image');
            // Revert preview
            setImagePreview(formData.imageUrl || null);
        } finally {
            setIsUploading(false);
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    }, [formData.imageUrl]);

    const handleRemoveImage = useCallback(() => {
        setFormData(prev => ({
            ...prev,
            imageUrl: '',
            imageUrlLow: ''
        }));
        setImagePreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, []);

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!formData.geniusPersonId) {
            toast.error('Genius Person ID is required');
            return;
        }
        if (!formData.firstName || !formData.lastName) {
            toast.error('First and last name are required');
            return;
        }

        onSubmit({
            ...formData,
            geniusPersonId: parseInt(formData.geniusPersonId)
        });
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-5">
            {/* Genius Link Section */}
            {showGeniusSearch && (
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <div className="flex items-center gap-2 mb-3">
                        <Link size={18} className="text-blue-600" />
                        <span className="font-medium">Link to Genius Sports Person</span>
                    </div>

                    <div className="flex items-center gap-2 mb-2">
                        <select
                            value={formData.personType}
                            onChange={(e) => setFormData({ ...formData, personType: e.target.value })}
                            className="input w-28"
                        >
                            <option value="player">Player</option>
                            <option value="staff">Staff</option>
                        </select>
                        <div className="relative flex-1">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                            <input
                                type="text"
                                value={geniusSearch}
                                onChange={(e) => setGeniusSearch(e.target.value)}
                                placeholder="Search Genius Sports..."
                                className="input w-full pl-10"
                            />
                        </div>
                    </div>

                    {geniusLoading ? (
                        <div className="flex justify-center py-4">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                        </div>
                    ) : geniusResults?.data?.length > 0 ? (
                        <div className="max-h-48 overflow-y-auto space-y-1">
                            {geniusResults.data.map((p) => (
                                <button
                                    key={p._id}
                                    type="button"
                                    onClick={() => handleGeniusSelect(p)}
                                    className="w-full flex items-center gap-2 p-2 text-left rounded hover:bg-blue-100 dark:hover:bg-blue-800/30"
                                >
                                    <span className="font-mono text-xs text-blue-600">
                                        #{p.geniusPersonId}
                                    </span>
                                    <span>
                                        {p.firstName} {p.lastName}
                                    </span>
                                    {p.position && (
                                        <Badge variant="info" className="text-xs">{p.position}</Badge>
                                    )}
                                </button>
                            ))}
                        </div>
                    ) : geniusSearch.length >= 2 ? (
                        <p className="text-sm text-muted-light dark:text-muted-dark text-center py-2">
                            No results found
                        </p>
                    ) : (
                        <p className="text-sm text-muted-light dark:text-muted-dark text-center py-2">
                            Type at least 2 characters to search
                        </p>
                    )}

                    <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-700">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                            Or enter Genius Person ID manually
                        </label>
                        <Input
                            type="number"
                            value={formData.geniusPersonId}
                            onChange={(e) => setFormData({ ...formData, geniusPersonId: e.target.value })}
                            placeholder="e.g. 123456"
                        />
                    </div>
                </div>
            )}

            {/* Show selected genius ID */}
            {!showGeniusSearch && formData.geniusPersonId && (
                <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                    <div className="flex items-center gap-2">
                        <Link size={16} className="text-green-600" />
                        <span className="text-sm">
                            Linked to Genius ID: <span className="font-mono font-medium">{formData.geniusPersonId}</span>
                        </span>
                    </div>
                    {!person && (
                        <button
                            type="button"
                            onClick={() => setShowGeniusSearch(true)}
                            className="text-sm text-green-600 hover:text-green-700"
                        >
                            Change
                        </button>
                    )}
                </div>
            )}

            {/* Person Type (when editing) */}
            {person && (
                <div className="flex items-center gap-2">
                    <Badge variant={formData.personType === 'player' ? 'info' : 'purple'}>
                        {formData.personType}
                    </Badge>
                    <span className="text-sm text-muted-light dark:text-muted-dark">
                        (Type cannot be changed after creation)
                    </span>
                </div>
            )}

            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-4">
                <Input
                    label="First Name *"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    required
                />
                <Input
                    label="Last Name *"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    required
                />
            </div>

            {/* Player-specific fields */}
            {formData.personType === 'player' && (
                <div className="grid grid-cols-3 gap-4">
                    <Select
                        label="Position"
                        value={formData.position}
                        onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                    >
                        <option value="">Select Position</option>
                        <option value="G">Guard (G)</option>
                        <option value="F">Forward (F)</option>
                        <option value="C">Center (C)</option>
                        <option value="G-F">Guard-Forward</option>
                        <option value="F-C">Forward-Center</option>
                    </Select>
                    <Input
                        label="Shirt Number"
                        value={formData.shirtNumber}
                        onChange={(e) => setFormData({ ...formData, shirtNumber: e.target.value })}
                        placeholder="e.g. 23"
                    />
                    <Input
                        label="Height"
                        value={formData.height}
                        onChange={(e) => setFormData({ ...formData, height: e.target.value })}
                        placeholder="e.g. 198cm"
                    />
                </div>
            )}

            {/* Staff-specific fields */}
            {formData.personType === 'staff' && (
                <Select
                    label="Role"
                    value={formData.staffRole}
                    onChange={(e) => setFormData({ ...formData, staffRole: e.target.value })}
                >
                    <option value="">Select Role</option>
                    <option value="Head Coach">Head Coach</option>
                    <option value="Assistant Coach">Assistant Coach</option>
                    <option value="Team Manager">Team Manager</option>
                    <option value="Athletic Trainer">Athletic Trainer</option>
                    <option value="Other">Other</option>
                </Select>
            )}

            {/* Nationality */}
            <div className="grid grid-cols-2 gap-4">
                <Input
                    label="Nationality"
                    value={formData.nationality}
                    onChange={(e) => setFormData({ ...formData, nationality: e.target.value })}
                    placeholder="e.g. Lebanon"
                />
                <Input
                    label="Nationality Code"
                    value={formData.nationalityCode}
                    onChange={(e) => setFormData({ ...formData, nationalityCode: e.target.value.toUpperCase() })}
                    placeholder="e.g. LBN"
                    maxLength={3}
                />
            </div>

            {/* Image Upload Section */}
            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Person Photo
                </label>
                <div className="flex gap-4 items-start">
                    {/* Image Preview */}
                    <div className="relative">
                        <div className={`w-28 h-28 rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-800 flex items-center justify-center border-2 ${isUploading ? 'border-blue-400' : 'border-gray-200 dark:border-gray-700'}`}>
                            {isUploading ? (
                                <div className="flex flex-col items-center gap-2">
                                    <Loader2 size={24} className="animate-spin text-blue-500" />
                                    <span className="text-xs text-gray-500">Uploading...</span>
                                </div>
                            ) : imagePreview ? (
                                <img
                                    src={imagePreview}
                                    alt="Preview"
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <User size={40} className="text-gray-400" />
                            )}
                        </div>
                        {imagePreview && !isUploading && (
                            <button
                                type="button"
                                onClick={handleRemoveImage}
                                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow-md"
                            >
                                <X size={14} />
                            </button>
                        )}
                    </div>

                    {/* Upload Controls */}
                    <div className="flex-1 space-y-3">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/gif,image/webp"
                            onChange={handleImageSelect}
                            className="hidden"
                            id="person-image-upload"
                        />
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isUploading}
                            className="w-full"
                        >
                            <Upload size={16} className="mr-2" />
                            {imagePreview ? 'Change Photo' : 'Upload Photo'}
                        </Button>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            Accepts JPEG, PNG, GIF, or WebP. Max 10MB.
                            <br />
                            Images are automatically resized and optimized.
                        </p>
                    </div>
                </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-border-light dark:border-border-dark">
                <Button type="button" variant="secondary" onClick={onCancel}>
                    Cancel
                </Button>
                <Button type="submit" disabled={isLoading || isUploading}>
                    {isLoading ? 'Saving...' : person ? 'Update Person' : 'Create Person'}
                </Button>
            </div>
        </form>
    );
}

export default function CustomPeopleManagement() {
    const queryClient = useQueryClient();
    const [typeFilter, setTypeFilter] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingPerson, setEditingPerson] = useState(null);
    const [deletingPerson, setDeletingPerson] = useState(null);

    // Fetch custom people
    const { data: peopleData, isLoading } = useQuery({
        queryKey: ['custom-people', typeFilter],
        queryFn: () => getCustomPeople(typeFilter || undefined)
    });

    // Create mutation
    const createMutation = useMutation({
        mutationFn: createCustomPerson,
        onSuccess: () => {
            toast.success('Custom person created');
            setShowCreateModal(false);
            queryClient.invalidateQueries(['custom-people']);
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to create person');
        }
    });

    // Update mutation
    const updateMutation = useMutation({
        mutationFn: ({ personId, data }) => updateCustomPerson(personId, data),
        onSuccess: () => {
            toast.success('Custom person updated');
            setEditingPerson(null);
            queryClient.invalidateQueries(['custom-people']);
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to update person');
        }
    });

    // Delete mutation
    const deleteMutation = useMutation({
        mutationFn: deleteCustomPerson,
        onSuccess: () => {
            toast.success('Custom person deleted');
            setDeletingPerson(null);
            queryClient.invalidateQueries(['custom-people']);
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to delete person');
        }
    });

    const columns = [
        {
            header: 'Person',
            accessor: 'name',
            render: (person) => (
                <div className="flex items-center gap-3">
                    {person.imageUrl ? (
                        <img
                            src={person.imageUrl}
                            alt={`${person.firstName} ${person.lastName}`}
                            className="w-10 h-10 rounded-full object-cover"
                        />
                    ) : (
                        <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                            <User size={20} className="text-gray-400" />
                        </div>
                    )}
                    <div>
                        <div className="font-medium">
                            {person.firstName} {person.lastName}
                        </div>
                        <div className="text-xs font-mono text-muted-light dark:text-muted-dark">
                            Genius ID: {person.geniusPersonId}
                        </div>
                    </div>
                </div>
            )
        },
        {
            header: 'Type',
            accessor: 'personType',
            render: (person) => (
                <Badge variant={person.personType === 'player' ? 'info' : 'purple'}>
                    {person.personType === 'player' ? (
                        <><Users size={12} className="mr-1" /> Player</>
                    ) : (
                        <><Briefcase size={12} className="mr-1" /> Staff</>
                    )}
                </Badge>
            )
        },
        {
            header: 'Position/Role',
            accessor: 'position',
            render: (person) => (
                <span className="text-sm">
                    {person.personType === 'player'
                        ? person.position || '-'
                        : person.staffRole || '-'
                    }
                </span>
            )
        },
        {
            header: 'Number',
            accessor: 'shirtNumber',
            render: (person) => (
                <span className="font-mono">
                    {person.shirtNumber ? `#${person.shirtNumber}` : '-'}
                </span>
            )
        },
        {
            header: 'Actions',
            accessor: 'actions',
            render: (person) => (
                <div className="flex items-center gap-2">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setEditingPerson(person);
                        }}
                        className="p-2 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                    >
                        <Edit size={16} />
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setDeletingPerson(person);
                        }}
                        className="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    >
                        <Trash2 size={16} />
                    </button>
                </div>
            )
        }
    ];

    const people = peopleData?.data || [];
    const playerCount = people.filter(p => p.personType === 'player').length;
    const staffCount = people.filter(p => p.personType === 'staff').length;

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold mb-2">Custom People</h1>
                    <p className="text-muted-light dark:text-muted-dark">
                        Manage custom players and staff linked to Genius Sports
                    </p>
                </div>
                <Button onClick={() => setShowCreateModal(true)}>
                    <UserPlus size={18} className="mr-2" />
                    Add Person
                </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
                <Card
                    className={`cursor-pointer transition-all ${!typeFilter ? 'ring-2 ring-primary' : ''}`}
                    onClick={() => setTypeFilter('')}
                >
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800">
                            <User size={20} className="text-gray-600" />
                        </div>
                        <div>
                            <p className="text-sm text-muted-light dark:text-muted-dark">Total</p>
                            <p className="text-2xl font-bold">{people.length}</p>
                        </div>
                    </div>
                </Card>
                <Card
                    className={`cursor-pointer transition-all ${typeFilter === 'player' ? 'ring-2 ring-primary' : ''}`}
                    onClick={() => setTypeFilter('player')}
                >
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                            <Users size={20} className="text-blue-600" />
                        </div>
                        <div>
                            <p className="text-sm text-muted-light dark:text-muted-dark">Players</p>
                            <p className="text-2xl font-bold">{playerCount}</p>
                        </div>
                    </div>
                </Card>
                <Card
                    className={`cursor-pointer transition-all ${typeFilter === 'staff' ? 'ring-2 ring-primary' : ''}`}
                    onClick={() => setTypeFilter('staff')}
                >
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                            <Briefcase size={20} className="text-purple-600" />
                        </div>
                        <div>
                            <p className="text-sm text-muted-light dark:text-muted-dark">Staff</p>
                            <p className="text-2xl font-bold">{staffCount}</p>
                        </div>
                    </div>
                </Card>
            </div>

            {/* Table */}
            <Card>
                <Table
                    columns={columns}
                    data={people}
                    loading={isLoading}
                    emptyState={{
                        title: 'No custom people',
                        description: 'Create your first custom person to get started'
                    }}
                />
            </Card>

            {/* Create Modal */}
            <Modal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                title="Create Custom Person"
                size="large"
            >
                <PersonForm
                    onSubmit={(data) => createMutation.mutate(data)}
                    onCancel={() => setShowCreateModal(false)}
                    isLoading={createMutation.isPending}
                />
            </Modal>

            {/* Edit Modal */}
            <Modal
                isOpen={!!editingPerson}
                onClose={() => setEditingPerson(null)}
                title="Edit Custom Person"
                size="large"
            >
                {editingPerson && (
                    <PersonForm
                        person={editingPerson}
                        onSubmit={(data) => updateMutation.mutate({
                            personId: editingPerson._id,
                            data
                        })}
                        onCancel={() => setEditingPerson(null)}
                        isLoading={updateMutation.isPending}
                    />
                )}
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                isOpen={!!deletingPerson}
                onClose={() => setDeletingPerson(null)}
                title="Delete Custom Person"
                size="small"
                footer={
                    <>
                        <Button variant="secondary" onClick={() => setDeletingPerson(null)}>
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            onClick={() => deleteMutation.mutate(deletingPerson._id)}
                            disabled={deleteMutation.isPending}
                        >
                            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                        </Button>
                    </>
                }
            >
                <p>
                    Are you sure you want to delete{' '}
                    <strong>{deletingPerson?.firstName} {deletingPerson?.lastName}</strong>?
                </p>
                <p className="text-sm text-muted-light dark:text-muted-dark mt-2">
                    This will also remove them from any team rosters they're assigned to.
                </p>
            </Modal>
        </div>
    );
}

